"use client";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { type Preloaded, useMutation, usePreloadedQuery } from "convex/react";
import { useEffect, useState } from "react";
import { InFeedAd, LeaderboardAd } from "@/components/ads";
import { CreatorStreamEmbed } from "@/components/creator-stream-embed";
import { NotFoundState } from "@/components/not-found-state";
import { WeaponDetailPanel } from "@/components/weapon/weapon-detail-panel";
import { adSlots } from "@/lib/ad-slots";
import type { WeaponDetail } from "@/types/weapon";
import { WeaponBuildComments } from "./weapon-build-comments";
import { WeaponBuildDescription } from "./weapon-build-description";
import { WeaponBuildHeader } from "./weapon-build-header";

type Props = {
  preloadedBuild: Preloaded<
    typeof api.weaponBuilds.queries.getWeaponBuildBySlug
  >;
};

export function WeaponBuildDetail({ preloadedBuild }: Props) {
  const build = usePreloadedQuery(preloadedBuild);
  const incrementViews = useMutation(
    api.weaponBuilds.mutations.incrementWeaponBuildViews
  );
  const [hasIncrementedView, setHasIncrementedView] = useState(false);

  useEffect(() => {
    if (build?._id && !hasIncrementedView) {
      const sessionKey = `viewed_weapon_build_${build._id}`;
      if (!sessionStorage.getItem(sessionKey)) {
        incrementViews({ weaponBuildId: build._id as Id<"weaponBuilds"> })
          .then(() => {
            sessionStorage.setItem(sessionKey, "true");
            setHasIncrementedView(true);
          })
          .catch(() => {
            // Error incrementing views - silently fail as it's not critical
          });
      }
    }
  }, [build?._id, incrementViews, hasIncrementedView]);

  // Handle not found state
  if (!build) {
    return <NotFoundState />;
  }

  // Transform weapon build data to WeaponDetail format for WeaponDetailPanel
  const weaponDetail: WeaponDetail = {
    weapon: {
      _id: build.weapon._id,
      name: build.weapon.name,
      category: build.weapon.category,
      attachmentPoints: build.weapon.attachmentPoints,
      logoUrl: build.weapon.logoUrl,
      imageUrl: build.weapon.imageUrl,
      baseStats: build.weapon.baseStats,
    },
    attachments: build.attachments,
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-5">
        {/* Breadcrumb */}
        <div className="font-mono text-[11px] tracking-wider">
          <span className="text-[#8B9297]">WEAPONS</span>
          <span className="text-[#6a7075]"> / </span>
          <span className="text-[#8B9297]">BUILDS</span>
          <span className="text-[#6a7075]"> / </span>
          <span className="text-[#F5A23E]">{build.name.toUpperCase()}</span>
        </div>

        {/* Header (includes voting) */}
        <WeaponBuildHeader build={build} />

        {/* Creator Stream Embed */}
        <CreatorStreamEmbed creatorId={build.user._id} />

        {/* Weapon Display */}
        <WeaponDetailPanel label="WEAPON BUILD" weaponDetail={weaponDetail} />

        {/* Description */}
        {build.description ? (
          <WeaponBuildDescription description={build.description} />
        ) : null}

        {adSlots.detail.leaderboard && (
          <LeaderboardAd slot={adSlots.detail.leaderboard} />
        )}

        {/* Comments */}
        <WeaponBuildComments weaponBuildId={build._id} />

        {adSlots.detail.inArticle && (
          <InFeedAd slot={adSlots.detail.inArticle} />
        )}
      </div>
    </div>
  );
}
